
export const QUESTION_TOPICS = [
  // First topic is the only one free for guests
  "API Rate Limiter (e.g., for Firebase/GitHub)",
  "URL Shortening Service (e.g., TinyURL)",
  "Chat Application (e.g., Messenger, WhatsApp)",
  "Content Delivery Network (CDN)",
  "Ride-Sharing Service (e.g., Uber, Lyft)",
  // Premium Tier (starts from index 5, or index 1 for guests if counting allowed topics)
  "Coordination System (e.g., ZooKeeper)",
  "Distributed Cache",
  "Distributed Locking Service (e.g., Google Chubby)",
  "E-commerce Site",
  "File-Sharing Service (e.g., Google Drive, Dropbox)",
  "Food-Delivery Service (e.g., Uber Eats, DoorDash)",
  "Job Scheduling System",
  "Notification Service",
  "Online Multiplayer Game System",
  "Payment Gateway (e.g., Stripe)",
  "Proximity Service (e.g., Yelp, Nearby Places)",
  "Pub/Sub System (e.g., Kafka)",
  "Recommendation Service",
  "Scalable Distributed Storage System (e.g., Bigtable)",
  "Search Typeahead / Autocomplete",
  "Social Media Newsfeed Service",
  "Social Media Platform (e.g., Facebook, Instagram, Twitter)",
  "Social Network & Message Board (e.g., Quora, Reddit)",
  "Video Conferencing Service (e.g., Zoom, Google Meet)",
  "Video Streaming Service (e.g., YouTube, Netflix)",
  "Web Crawler",
  "Web Mapping Service (e.g., Google Maps)",
];

export const DIFFICULTY_LEVELS = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

export const SYSTEM_SCALES = [
  "Startup (10k users, limited features)",
  "Small Company (100k users, moderate features)",
  "Medium Enterprise (1M users, growing features)",
  "Large Corporation (10M users, complex features)",
  "Global Scale (100M+ users, extensive features, high availability)",
];

export const PRACTICE_TIMER_DURATION = 45 * 60; // 45 minutes in seconds

// --- User Access Limits ---

// Logged-in Free User Limits
export const FREE_USER_TOPIC_LIMIT = 5; // Number of topics free users can access from the start of QUESTION_TOPICS
export const FREE_USER_QUESTION_LIMIT = 3; // Max questions a free logged-in user can generate


// --- Pricing Model Tiers and Limits ---
export type UserTier = 'free' | 'basic' | 'standard' | 'premium' | 'payPerUseSessionActive';
export type CurrencyCode = 'USD' | 'INR' | 'GBP' | 'EUR'; // Added GBP & EUR

export interface PriceDetail {
  monthly?: number;
  annual?: number;
  session?: number; 
  daily?: number; 
  currencySymbol: string;
}

export interface TierPriceData {
  [key: string]: PriceDetail;
}


export const PRICING_TIERS = {
  FREE: 'free',
  BASIC: 'basic',
  STANDARD: 'standard',
  PREMIUM: 'premium',
  PAY_PER_USE: 'payPerUseSessionActive',
} as const;

export interface TierConfig {
  name: string;
  prices: TierPriceData;
  repliesPerMonth?: number | 'unlimited';
  imagesPerMonth?: number | 'unlimited';
  hintsHelpsPerMonth?: number | 'unlimited';
  dailyRepliesLimit?: number | 'unlimited'; 
  dailyImagesLimit?: number; 
  dailyHintsHelpsLimit?: number | 'unlimited'; 
  feedbackType: 'basic' | 'full';
  ads: boolean;
  prioritySupport?: boolean;
  advancedAnalytics?: boolean;
  downloadableReports?: boolean;
  description: string;
  sessionDurationDays?: number;
}

export type TierDetailsType = Record<UserTier | 'payPerUseOption', TierConfig>;


export const TIER_DETAILS: TierDetailsType = {
  [PRICING_TIERS.FREE]: {
    name: "Free",
    prices: {
      USD: { currencySymbol: '$' },
      INR: { currencySymbol: '₹' },
      GBP: { currencySymbol: '£' },
      EUR: { currencySymbol: '€' },
    },
    repliesPerMonth: 20,
    imagesPerMonth: 5,
    hintsHelpsPerMonth: 2,
    feedbackType: 'basic',
    ads: true,
    description: "Get started with core features.",
  },
  [PRICING_TIERS.BASIC]: {
    name: "Basic",
    prices: {
      USD: { monthly: 9, annual: 99, currencySymbol: '$' },
      INR: { monthly: 699, annual: 6999, currencySymbol: '₹' },
      GBP: { monthly: 7, annual: 79, currencySymbol: '£' },
      EUR: { monthly: 8, annual: 89, currencySymbol: '€' },
    },
    repliesPerMonth: 100,
    imagesPerMonth: 50,
    hintsHelpsPerMonth: 100,
    feedbackType: 'full',
    ads: false, // Basic tier is Ad-Free
    description: "More replies and full feedback.",
  },
  [PRICING_TIERS.STANDARD]: {
    name: "Standard",
    prices: {
      USD: { monthly: 19, annual: 199, currencySymbol: '$' },
      INR: { monthly: 1599, annual: 15999, currencySymbol: '₹' },
      GBP: { monthly: 15, annual: 159, currencySymbol: '£' },
      EUR: { monthly: 17, annual: 179, currencySymbol: '€' },
    },
    repliesPerMonth: 300,
    imagesPerMonth: 150,
    hintsHelpsPerMonth: 300,
    feedbackType: 'full',
    ads: false,
    prioritySupport: true,
    description: "For frequent users, with more capacity and support.",
  },
  [PRICING_TIERS.PREMIUM]: {
    name: "Premium",
    prices: {
      USD: { monthly: 29, annual: 299, currencySymbol: '$' },
      INR: { monthly: 2499, annual: 24999, currencySymbol: '₹' },
      GBP: { monthly: 23, annual: 239, currencySymbol: '£' },
      EUR: { monthly: 27, annual: 279, currencySymbol: '€' },
    },
    repliesPerMonth: 1000,
    imagesPerMonth: 500,
    hintsHelpsPerMonth: 1000,
    feedbackType: 'full',
    ads: false,
    prioritySupport: true,
    advancedAnalytics: true,
    downloadableReports: true,
    description: "Highest capacity and advanced features.",
  },
  payPerUseOption: { // This is the definition for the pricing page display
    name: "Day Pass",
    prices: {
      USD: { daily: 2.99, currencySymbol: '$' }, 
      INR: { daily: 249, currencySymbol: '₹' },  
      GBP: { daily: 2.49, currencySymbol: '£' }, 
      EUR: { daily: 2.79, currencySymbol: '€' }, 
    },
    dailyRepliesLimit: 30,    
    dailyImagesLimit: 10,     
    dailyHintsHelpsLimit: 5,  
    feedbackType: 'full',
    ads: true, // Day Pass Option shows ads (will see ads if purchased)
    description: "Unlock full features with daily usage quotas for 24 hours.", 
    sessionDurationDays: 1,
  },
  [PRICING_TIERS.PAY_PER_USE]: { // This is the "active state" tier
    name: "Day Pass Active",
    prices: { 
      USD: { daily: 2.99, currencySymbol: '$' },
      INR: { daily: 249, currencySymbol: '₹' },
      GBP: { daily: 2.49, currencySymbol: '£' },
      EUR: { daily: 2.79, currencySymbol: '€' },
    },
    dailyRepliesLimit: 30,
    dailyImagesLimit: 10,
    dailyHintsHelpsLimit: 5,
    feedbackType: 'full',
    ads: true, // Active Day Pass state also shows ads
    description: "Currently on an active Day Pass with daily quotas.", 
    sessionDurationDays: 1,
  }
};

export interface AddonPriceDetail {
  price: number;
  currencySymbol: string;
}

export interface AddonPriceData {
  [key: string]: AddonPriceDetail;
}

export const ADDON_PRICES: Record<string, AddonPriceData> = {
  EXTRA_10_REPLIES: {
    USD: { price: 0.50, currencySymbol: '$' },
    INR: { price: 40, currencySymbol: '₹' },
    GBP: { price: 0.40, currencySymbol: '£' },
    EUR: { price: 0.45, currencySymbol: '€' },
  },
  EXTRA_5_IMAGES: {
    USD: { price: 0.50, currencySymbol: '$' },
    INR: { price: 40, currencySymbol: '₹' },
    GBP: { price: 0.40, currencySymbol: '£' },
    EUR: { price: 0.45, currencySymbol: '€' },
  },
  FULL_FEEDBACK_REPORT: {
    USD: { price: 0.50, currencySymbol: '$' },
    INR: { price: 40, currencySymbol: '₹' },
    GBP: { price: 0.40, currencySymbol: '£' },
    EUR: { price: 0.45, currencySymbol: '€' },
  },
  EXTRA_5_HINTS_HELPS: {
    USD: { price: 1.00, currencySymbol: '$' },
    INR: { price: 80, currencySymbol: '₹' },
    GBP: { price: 0.80, currencySymbol: '£' },
    EUR: { price: 0.90, currencySymbol: '€' },
  },
};

export const REFERRAL_SIGNUP_BONUS_REFERRER = {
  replies: 5,
  images: 5,
  hintsHelps: 1,
};

export const REFEREE_SIGNUP_BONUS = {
  replies: 3,
  images: 2,
  hintsHelps: 1,
};

export const DEFAULT_COUPON_TIER: UserTier = PRICING_TIERS.BASIC;
export const DEFAULT_COUPON_DURATION_DAYS = 30;

export const DEFAULT_RAZORPAY_INTEGRATION_ENABLED = true;
export const DEFAULT_MAINTENANCE_MODE_ACTIVE = false;


